import { ScholarshipDetail } from '@common-types';
import { CoreBox, CoreTypography } from '@youscience/core';
import { isUndefined } from 'lodash-es';

import { Container } from '@components/Layout/components/Container';

import EllipseIcon from '@images/ellipse.svg?react';
import { getMonthName, moneyFormatter } from '@utils/formatter';

import { StyledCategoryItem } from '../Requirements/Requirements.styles';
import { StyledAboutSection, StyledInformationWrap, StyledTextWrap, sxStyles } from './AboutSection.styles';

const renderInfoItem = (fieldName: string, value: string) => {
  if (!value) return null;

  return (
    <CoreBox sx={sxStyles.infoItem}>
      <CoreTypography variant="body2">{fieldName}</CoreTypography>

      <CoreTypography variant="h6" component="h5">
        {value}
      </CoreTypography>
    </CoreBox>
  );
};

const renderBulletPoint = (value: string) => {
  return (
    <StyledCategoryItem>
      <CoreBox>
        <EllipseIcon />
      </CoreBox>
      <CoreTypography variant="h6" component="h5">
        {value}
      </CoreTypography>
    </StyledCategoryItem>
  );
};

const renderWhoCanApply = (applicantRequiredLabel: string, highSchoolEligiblityLabel: string) => {
  return (
    <CoreBox sx={sxStyles.infoItem}>
      <CoreTypography variant="body2">Who can apply</CoreTypography>

      {applicantRequiredLabel && renderBulletPoint(applicantRequiredLabel)}
      {highSchoolEligiblityLabel && renderBulletPoint(highSchoolEligiblityLabel)}
    </CoreBox>
  );
};

const getApplicantRequiredLabel = (required: boolean | null) => {
  return required
    ? 'Applicant must currently be in high school'
    : 'Applicant does not have to be currently in high school';
};

const getHighSchoolEligiblityLabel = (eligible: boolean | null) => {
  return eligible ? 'Everyone including high schools students' : 'Everyone except high schools students';
};

interface AboutSectionProps {
  scholarship: ScholarshipDetail;
}

export const AboutSection = (props: AboutSectionProps) => {
  const {
    description,
    applicationRequirements,
    disabilityType,
    applicantRequiredInHighSchool,
    applicantNotRequiredInHighSchool,
    areHighSchoolEligible,
    areHighSchoolNotEligible,
    applicationDates,
    moneyRange,
  } = props.scholarship;

  const hasDeadline =
    applicationDates?.deadlineDay && applicationDates?.deadlineMonth && applicationDates?.deadlineYear;
  const formattedDeadline = `${getMonthName(applicationDates?.deadlineMonth as number)} ${
    applicationDates?.deadlineDay
  }${applicationDates?.deadlineYear ? ',' : ''} ${applicationDates?.deadlineYear || ''}`;

  const showWhoCanApply =
    applicantRequiredInHighSchool !== applicantNotRequiredInHighSchool ||
    areHighSchoolEligible !== areHighSchoolNotEligible;
  const applicantRequiredLabel =
    applicantRequiredInHighSchool !== applicantNotRequiredInHighSchool
      ? getApplicantRequiredLabel(applicantRequiredInHighSchool)
      : '';
  const highSchoolEligiblityLabel =
    areHighSchoolEligible !== areHighSchoolNotEligible ? getHighSchoolEligiblityLabel(areHighSchoolEligible) : '';

  const essayNeededText = applicationRequirements?.essay ? 'Required' : 'Non-essay';
  const additionalInformation =
    disabilityType && Object.values(disabilityType).some((type) => type) ? 'For people with disabilities' : '';
  const fullMoneyRange =
    moneyRange?.lowerLimit && moneyRange?.upperLimit
      ? `${moneyFormatter.format(moneyRange?.lowerLimit)} – ${moneyFormatter.format(moneyRange?.upperLimit)}`
      : moneyFormatter.format(moneyRange?.lowerLimit as number);

  return (
    <Container>
      <StyledAboutSection>
        <StyledTextWrap>
          <CoreTypography sx={sxStyles.title} variant="h3" component="h2">
            About
          </CoreTypography>

          <CoreTypography sx={sxStyles.description}>{description}</CoreTypography>
        </StyledTextWrap>

        <StyledInformationWrap>
          <CoreBox>
            {hasDeadline && renderInfoItem('Deadline', formattedDeadline)}

            {moneyRange?.lowerLimit && renderInfoItem('Award', fullMoneyRange)}

            {renderInfoItem('Essay needed', isUndefined(applicationRequirements?.essay) ? '–' : essayNeededText)}
          </CoreBox>

          <CoreBox>
            {showWhoCanApply ? renderWhoCanApply(applicantRequiredLabel, highSchoolEligiblityLabel) : null}

            {renderInfoItem('Additional information', additionalInformation)}
          </CoreBox>
        </StyledInformationWrap>
      </StyledAboutSection>
    </Container>
  );
};
